import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Story, StoryFrame } from './api';

export interface StoryPosition {
  /** Identity, not an index: the list can be refetched underneath us. */
  storyId: string;
  frameId: string;
}

export interface StoryPlayerState {
  storyIndex: number;
  frameIndex: number;
  frameCount: number;
  /** 0-1 through the current frame. Drives the progress bar. */
  progress: number;
  isPaused: boolean;
  /** The user pressed pause, as opposed to holding or buffering. */
  isPausedByUser: boolean;
  isBuffering: boolean;
  mediaFailed: boolean;
}

interface UseStoryPlayerOptions {
  stories: Story[];
  initialStorySlug: string;
  onExit: () => void;
  onStoryChange?: ((story: Story) => void) | undefined;
}

/** A single rAF delta is never allowed to cross more than this. */
const MaxTickMs = 100;

/** How long to wait for artwork before giving up and playing anyway. */
const BufferCeilingMs = 7000;

/** Frame durations arrive from an API and are clamped before they reach the clock. */
const MinFrameMs = 1000;
const MaxFrameMs = 30_000;
const DefaultFrameSeconds = 5;

/**
 * The state machine behind the story viewer.
 *
 * Four decisions here are load-bearing, each of them a bug that a reasonable
 * implementation ships without noticing.
 *
 * **Position is identity, not an index.** The story list is a live query; a
 * refetch can drop an expired story or reorder the strip. An index would then
 * point at a different site mid-story. Slug and frame id survive that.
 *
 * **Timing runs on requestAnimationFrame with a clamped delta.** rAF stops while
 * the tab is hidden, which gives a free pause — but on return the first callback
 * arrives with a timestamp minutes later, and an unclamped delta would advance
 * through every remaining frame in one tick. Clamping means backgrounded time is
 * simply never credited.
 *
 * **The clock waits for the artwork, but not forever.** A frame that starts its
 * five seconds while the image is still downloading shows a black rectangle for
 * three of them. A frame that waits on a stalled CDN connection freezes the
 * whole viewer. Both are wrong, so there is a gate with a ceiling.
 *
 * **Pausing is an explicit, keyboard-reachable control.** Press-and-hold is not
 * discoverable and not operable without a pointer; WCAG 2.2.2 wants a real
 * mechanism for anything that moves on its own.
 */
export function useStoryPlayer({
  stories,
  initialStorySlug,
  onExit,
  onStoryChange,
}: UseStoryPlayerOptions) {
  const [position, setPosition] = useState<StoryPosition | null>(null);
  const [progress, setProgress] = useState(0);
  const [isHeld, setIsHeld] = useState(false);
  const [isPausedByUser, setIsPausedByUser] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [mediaFailed, setMediaFailed] = useState(false);

  const prefersReducedMotion = usePrefersReducedMotion();

  // --- resolve the position against the current list ------------------------
  const storyIndex = position
    ? Math.max(
        0,
        stories.findIndex((s) => s.id === position.storyId),
      )
    : Math.max(
        0,
        stories.findIndex((s) => s.siteSlug === initialStorySlug),
      );

  const story = stories[storyIndex];

  const frameIndex = position
    ? Math.max(0, story?.frames.findIndex((f) => f.id === position.frameId) ?? 0)
    : 0;

  const frame: StoryFrame | undefined = story?.frames[frameIndex];

  // Elapsed lives in a ref: the rAF loop writes it every frame, and holding it
  // in state would re-render sixty times a second.
  const elapsedRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const playingFrameIdRef = useRef<string | null>(null);

  // The clock reaches `next` through a ref rather than through its dependency
  // array.
  //
  // Without this, the effect re-runs whenever `next` changes identity — and
  // `next` depends on `onExit`, which a caller can easily pass inline. The
  // re-run tears down the rAF loop and clears the timestamp on every tick, so
  // elapsed time never accumulates and the story simply never advances. It looks
  // like a paused viewer with a working progress bar, which is a maddening thing
  // to debug from a bug report.
  const nextRef = useRef<() => void>(() => undefined);

  const isPaused = isHeld || isPausedByUser || isBuffering || prefersReducedMotion;

  const moveTo = useCallback((nextStory: Story, nextFrame: StoryFrame) => {
    elapsedRef.current = 0;
    lastTickRef.current = null;
    setProgress(0);
    setIsBuffering(true);
    setMediaFailed(false);
    setPosition({ storyId: nextStory.id, frameId: nextFrame.id });
  }, []);

  const next = useCallback(() => {
    if (!story) {
      onExit();
      return;
    }

    const upcoming = story.frames[frameIndex + 1];

    if (upcoming) {
      moveTo(story, upcoming);
      return;
    }

    // End of this site's story: on to the next site, matching Instagram.
    for (let i = storyIndex + 1; i < stories.length; i++) {
      const candidate = stories[i];
      const first = candidate?.frames[0];

      // A story with no frames would render an empty screen and then be stuck,
      // so it is skipped rather than entered.
      if (candidate && first) {
        moveTo(candidate, first);
        return;
      }
    }

    onExit();
  }, [frameIndex, moveTo, onExit, stories, storyIndex, story]);

  const previous = useCallback(() => {
    if (!story) {
      onExit();
      return;
    }

    const earlier = story.frames[frameIndex - 1];

    if (earlier) {
      moveTo(story, earlier);
      return;
    }

    for (let i = storyIndex - 1; i >= 0; i--) {
      const candidate = stories[i];
      // Back into the previous site lands on its LAST frame: "back" undoes one
      // step, it does not restart a story.
      const last = candidate?.frames.at(-1);

      if (candidate && last) {
        moveTo(candidate, last);
        return;
      }
    }

    // Already at the very beginning. Restarting the frame beats a dead tap.
    if (frame) {
      moveTo(story, frame);
    }
  }, [frame, frameIndex, moveTo, onExit, stories, storyIndex, story]);

  // --- the clock ------------------------------------------------------------
  const frameId = frame?.id;
  const frameDurationSeconds = frame?.durationSeconds;

  useEffect(() => {
    if (!frameId || isPaused) {
      lastTickRef.current = null;
      return;
    }

    // Covers both an explicit move and the implicit fall-back: whenever the
    // frame identity changes, the clock starts from zero.
    if (playingFrameIdRef.current !== frameId) {
      playingFrameIdRef.current = frameId;
      elapsedRef.current = 0;
      lastTickRef.current = null;
    }

    // A 0, null or negative duration is broken data, not a request for an
    // instant frame — fall back to the default rather than flashing past. The
    // clamp then catches anything merely absurd.
    const seconds =
      frameDurationSeconds !== undefined && frameDurationSeconds > 0
        ? frameDurationSeconds
        : DefaultFrameSeconds;

    const durationMs = Math.min(MaxFrameMs, Math.max(MinFrameMs, seconds * 1000));

    function tick(now: number) {
      if (lastTickRef.current !== null) {
        // Clamped: a tab returning from the background delivers one enormous
        // delta, and crediting it would skip the rest of the story in a frame.
        elapsedRef.current += Math.min(MaxTickMs, now - lastTickRef.current);
      }

      lastTickRef.current = now;

      const ratio = Math.min(1, elapsedRef.current / durationMs);
      setProgress(ratio);

      if (ratio >= 1) {
        nextRef.current();
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      // Dropping the timestamp is what makes resuming continue from the banked
      // elapsed total rather than counting the paused interval as playback.
      lastTickRef.current = null;
    };
    // Deliberately narrow: frame identity and duration, plus whether we are
    // paused. Anything else in here restarts the clock on an unrelated render.
  }, [frameId, frameDurationSeconds, isPaused]);

  useEffect(() => {
    nextRef.current = next;
  }, [next]);

  // --- buffering ceiling ----------------------------------------------------
  useEffect(() => {
    if (!isBuffering) {
      return;
    }

    // A stalled connection — open, no bytes, no error — would otherwise hold the
    // clock forever and read as a crash.
    const timer = window.setTimeout(() => setIsBuffering(false), BufferCeilingMs);

    return () => window.clearTimeout(timer);
  }, [isBuffering, frameId]);

  // --- keep the URL in step -------------------------------------------------
  const lastReportedSlug = useRef<string | null>(null);

  useEffect(() => {
    if (story && story.siteSlug !== lastReportedSlug.current) {
      lastReportedSlug.current = story.siteSlug;
      onStoryChange?.(story);
    }
  }, [onStoryChange, story]);

  // --- the list changing underneath ----------------------------------------
  //
  // A story expiring or being unpublished mid-view needs no handling here: the
  // index is DERIVED from identity with a Math.max(0, findIndex) floor, so a
  // vanished story resolves to the first live one during render. Reconciling it
  // in an effect instead would set state inside a render pass and cascade.
  //
  // Only an empty list needs an escape, and that is a navigation rather than a
  // state change.
  useEffect(() => {
    if (stories.length === 0) {
      onExit();
    }
  }, [onExit, stories.length]);

  const state = useMemo<StoryPlayerState>(
    () => ({
      storyIndex,
      frameIndex,
      frameCount: story?.frames.length ?? 0,
      progress,
      isPaused,
      isPausedByUser,
      isBuffering,
      mediaFailed,
    }),
    [
      frameIndex,
      isBuffering,
      isPaused,
      isPausedByUser,
      mediaFailed,
      progress,
      story?.frames.length,
      storyIndex,
    ],
  );

  return {
    state,
    story,
    frame,
    prefersReducedMotion,
    next,
    previous,
    hold: useCallback(() => setIsHeld(true), []),
    release: useCallback(() => setIsHeld(false), []),
    togglePause: useCallback(() => setIsPausedByUser((paused) => !paused), []),
    onFrameReady: useCallback(() => setIsBuffering(false), []),
    onFrameFailed: useCallback(() => {
      // The artwork is gone, but the headline and the call to action still
      // convert — so the frame plays on with a branded fallback behind it.
      setMediaFailed(true);
      setIsBuffering(false);
    }, []),
  };
}

/**
 * Tracks the OS "reduce motion" setting, and keeps tracking it — it can be
 * turned on while the page is open, and a value read once at mount would miss
 * that.
 */
function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const listener = (event: MediaQueryListEvent) => setPrefers(event.matches);

    query.addEventListener('change', listener);

    return () => query.removeEventListener('change', listener);
  }, []);

  return prefers;
}
